import type { ReactNode } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import type { ModuleManifest } from '../modules';
import { resolveModules } from '../business-profile/businessProfileResolver';
import type { BusinessProfile } from '../business-profile/businessProfile';

interface ModuleRouteGuardProps {
  manifest: ModuleManifest;
  children: ReactNode;
}

export function ModuleRouteGuard({ manifest, children }: ModuleRouteGuardProps) {
  const { activeWorkspace, dbUser } = useAuth();
  const { permissions, isInitialized } = usePermissions();

  if (!isInitialized) {
    return <div className="p-6 text-sm text-slate-500">Carregando autorização...</div>;
  }

  if (manifest.status === 'disabled' || manifest.enabled === false) {
    return (
      <div className="flex min-h-[320px] items-center justify-center p-6">
        <div className="max-w-md rounded-2xl border border-rose-200 bg-rose-50 p-6 text-center">
          <h2 className="text-lg font-bold text-rose-900">Módulo desativado</h2>
          <p className="mt-2 text-sm text-rose-800">
            Este módulo está desativado no momento.
          </p>
        </div>
      </div>
    );
  }

  const isPlatformAdmin = dbUser?.isPlatformAdmin === true;

  const profile = activeWorkspace?.settings?.businessProfile as BusinessProfile | undefined;
  const enabledApps = profile
    ? resolveModules(profile)
    : Array.isArray(activeWorkspace?.settings?.enabledApps)
      ? (activeWorkspace.settings.enabledApps as string[])
      : [];
  const hasLegacyWorkspaceConfig = enabledApps.length === 0;
  const entitlementAllowed =
    isPlatformAdmin ||
    !manifest.requiresEntitlement ||
    hasLegacyWorkspaceConfig ||
    enabledApps.includes(manifest.id);
  const permissionsAllowed = (manifest.permissions ?? []).every((permission) =>
    permissions.some((grantedPermission) => grantedPermission === permission),
  );

  if (!entitlementAllowed) {
    return (
      <div className="flex min-h-[320px] items-center justify-center p-6">
        <div className="max-w-md rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center">
          <h2 className="text-lg font-bold text-amber-900">Acesso restrito</h2>
          <p className="mt-2 text-sm text-amber-800">
            Este aplicativo não está habilitado no workspace atual.
          </p>
          <p className="mt-1 text-xs text-amber-700">
            Entre em contato com o administrador da organização para habilitar este módulo.
          </p>
        </div>
      </div>
    );
  }

  if (!permissionsAllowed) {
    return (
      <div className="flex min-h-[320px] items-center justify-center p-6">
        <div className="max-w-md rounded-2xl border border-rose-200 bg-rose-50 p-6 text-center">
          <h2 className="text-lg font-bold text-rose-900">Acesso restrito</h2>
          <p className="mt-2 text-sm text-rose-800">
            Você não possui permissão para acessar este módulo.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
